import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { firstValueFrom, Observable, Subject, take } from "rxjs";
import { BmAnalyticsData, BudgetModel, BudgetModelSelectorMiniDTO } from "./pojo/bm-classes";
import { BudgetModelFormGroup } from "./pojo/bm-form";
import { OrganizationsService } from "../../organization/service/organizations.service";
import { NOTIFICATIONS, NotificationsService } from "../../../core/service/notifications.service";
import {
  UnsavedChangesModalService,
} from "../../shared/component/unsaved-changes-modal-wrapper/service/unsaved-changes-modal.service";
import { filter, takeUntil } from "rxjs/operators";
import { BmButtons } from "./pojo/bm-enums";
import { BudgetModelTemplate } from "../../excel/templates/budget-model/BudgetModelTemplate";
import { BudgetFormService } from "./services/general/bm-form.service";
import { BmDefaultFormCreatorService } from "./services/general/bm-default-form-creator.service";
import { BmGeneralService } from "./services/general/bm-general-service";
import { BudgetModelBackendService } from "./services/backend/budget-model-backend.service";
import { HeaderBudgetModelComponent } from "./budget-header/header-budget-model.component";
import { BmLoadingStateComponent } from "./loading-state/bm-loading-page/bm-loading-state.component";
import { TableBudgetModelComponent } from "./budget-table/table-budget-model.component";
import { CanDeactivateComponent } from "../../shared/component/can-deactivate-guard/can-deactivate-guard-interface";

import { BmAnalyticsComponent } from "./analytics/main/bm-analytics.component";
import { OrganizationDTO } from "../../organization/dto/OrganizationDTO";
import { NgClass } from "@angular/common";


@Component({
  selector: 'app-fund-budget-model',
  templateUrl: './budget-model.component.html',
  styleUrls: ['./budget-model.component.scss'],
  imports: [
    TableBudgetModelComponent,
    HeaderBudgetModelComponent,
    BmLoadingStateComponent,
    BmAnalyticsComponent,
    NgClass,
  ],
})
export class BudgetModelComponent implements OnInit, OnDestroy, CanDeactivateComponent {
  private budgetModelBackendService = inject(BudgetModelBackendService);
  private organizationsService = inject(OrganizationsService);
  private notificationsService = inject(NotificationsService);
  private unsavedChangesModalService = inject(UnsavedChangesModalService);

  private _destroyStream: Subject<void> = new Subject<void>();

  model: BudgetModel;
  budgetForm: BudgetModelFormGroup;
  budgetDTO: BudgetModel;
  selectedOrganization: OrganizationDTO;
  selectorData: BudgetModelSelectorMiniDTO[];
  analyticsData: BmAnalyticsData;
  loadingState = true;


  ngOnInit(): void {
    this.getOrganization();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
    // maybe it is useless, detailed investigation is needed
    this.model = null;
    this.budgetForm = null;
    this.budgetDTO = null;
    this.selectedOrganization = null;
    this.selectorData = null;
    this.analyticsData = null;
    this.loadingState = null;
  }

  getOrganization(): void {
    this.organizationsService.selected$
      .pipe(
        takeUntil(this._destroyStream),
        filter((org) => Boolean(org)))
      .subscribe((org) => {
        this.loadingState = true;
        this.selectedOrganization = org;
        this.getInitialModelData();
      });
  }

  async changeModel(modelUuid: string): Promise<void> {
    this.loadingState = true;
    this.budgetDTO = await firstValueFrom(this.budgetModelBackendService.get(modelUuid).pipe(takeUntil(this._destroyStream)));
    this.initModel();
  }

  buttonClicked(action: BmButtons): void {
    switch (action) {
      case BmButtons.CANCEL:
        this.loadingState = true;
        this.initModel();
        break;
      case BmButtons.ADD_NEW_MODEL:
        this.loadingState = true;
        this.addNewModel();
        break;
      case BmButtons.CREATE:
        this.loadingState = true;
        this.createModel();
        break;
      case BmButtons.UPDATE:
        this.loadingState = true;
        this.updateModel();
        break;
      case BmButtons.DELETE:
        this.loadingState = true;
        this.deleteModel();
        break;
      case BmButtons.DUPLICATE:
        this.loadingState = true;
        this.duplicateModel();
        break;
    }
  }

  /**
   * Prevent leaving the page when user has unsaved changes
   */
  canDeactivate(): Observable<boolean> | boolean {
    if (this.unsavedChangesModalService.getIsInEditMode()) {
      return this.unsavedChangesModalService.showConfirmDialog();
    }
    return true;
  }

  exportExcel(): void {
    const fileName = this.selectedOrganization.name + '_' + (this.budgetForm.value.name || 'Budget_Model') + '.xlsx';
    new BudgetModelTemplate().export(this.budgetForm, fileName);
  }

  private initModel(isNewModel = false, isInitialModelData = false): void {
    this.model = isNewModel ? BmDefaultFormCreatorService.createNewBudgetModelDefault() : this.budgetDTO;
    this.updateSelectorData(this.model);
    this.budgetForm = BudgetFormService.createBudgetForm(this.model, this.selectedOrganization, isNewModel);
    if (!isInitialModelData) {
      this.loadAnalytics();
    }
    this.budgetForm.valueChanges
      .pipe(take(1)) // only on first change
      .subscribe(() => {
        this.unsavedChangesModalService.setIsInEditMode(true);
      });
    setTimeout(() => {
      this.loadingState = false;
    }, 400); // set minimal loading time
  }

  private loadAnalytics(): void {
    if (this.analyticsData) {
      this.analyticsData.selectedFund = null;
      this.analyticsData.selectedPeriod = null;
    }
  }

  private async getInitialModelData(): Promise<void> {
    const modelWrapper = await firstValueFrom(this.budgetModelBackendService.getInitialModelData(this.selectedOrganization.uuid).pipe(takeUntil(this._destroyStream)));
    this.selectorData = modelWrapper.selectorData ?? [];
    this.budgetDTO = modelWrapper.selectedModel ?? null;
    this.analyticsData = modelWrapper.analyticsData ?? null;
    this.initModel(!modelWrapper.selectedModel, true);
  }

  private updateSelectorData(model: BudgetModel): void {
    if (!model.uuid) {
      return;
    }
    const selectedModel = this.selectorData.find((selectorModel) => selectorModel.uuid === model.uuid);
    if (selectedModel) {
      selectedModel.name = model.name;
      selectedModel.selectorText = BmGeneralService.createSelectorText(model);
    } else {
      const newModel = new BudgetModelSelectorMiniDTO();
      newModel.uuid = model.uuid;
      newModel.orgUuid = model.orgUuid;
      newModel.name = model.name;
      newModel.lastModifiedDate = model.lastModifiedDate;
      newModel.createdDate = model.createdDate;
      newModel.createdBy = model.createdBy;
      newModel.selectorText = BmGeneralService.createSelectorText(model);
      this.selectorData.push(newModel);
    }
  }

  private async createModel(): Promise<void> {
    this.budgetForm.controls.isEditState.patchValue(false);
    const dto = BudgetFormService.mapFormToDto(this.budgetForm);
    this.budgetDTO = await firstValueFrom(this.budgetModelBackendService.create(dto).pipe(takeUntil(this._destroyStream)));
    this.initModel();
    this.notificationsService.success(NOTIFICATIONS.BUDGET_MODEL_WAS_CREATED);
  }

  private async deleteModel(): Promise<void> {
    await firstValueFrom(this.budgetModelBackendService.delete(this.budgetForm.value.uuid).pipe(takeUntil(this._destroyStream)));
    this.notificationsService.success(NOTIFICATIONS.BUDGET_MODEL_DELETED_SUCCESSFULLY);
    this.getInitialModelData();
  }

  private async duplicateModel(): Promise<void> {
    this.budgetDTO = await firstValueFrom(this.budgetModelBackendService.duplicate(this.budgetForm.getRawValue().uuid).pipe(takeUntil(this._destroyStream)));
    this.initModel();
    this.notificationsService.success(NOTIFICATIONS.BUDGET_MODEL_DUPLICATED_SUCCESSFULLY);
  }

  private addNewModel(): void {
    this.initModel(true);
    this.budgetForm.controls.isEditState.patchValue(true);
  }

  private async updateModel(): Promise<void> {
    this.budgetForm.controls.isEditState.patchValue(false);
    const dto = BudgetFormService.mapFormToDto(this.budgetForm);
    this.budgetDTO = await firstValueFrom(this.budgetModelBackendService.update(dto).pipe(takeUntil(this._destroyStream)));
    this.initModel();
    this.notificationsService.success(NOTIFICATIONS.BUDGET_MODEL_UPDATED_SUCCESSFULLY);
  }
}
